import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { AppState } from '../../../../../reducers';
import {
  hasConsolidatedFinancialDataLoaded,
  hasCurrencyFinancialDataLoaded,
  hasDatabaseFinancialDataLoaded,
  hasFinancialInitialLoaded,
  selectFinancialCategoryNames,
  selectFinancialCurrencyData,
  selectFinancialData,
  selectFinancialDataCurrentDatabaseId,
  selectFinancialDataSelectedConsolidated,
  selectFinancialDataSelectedCurrencyId,
  selectFinancialDataSelectedDatabaseId,
  selectFinancialYearAlignmentData,
  selectHasSingleTypeOfFinancialsFlag,
} from '../drill-down.selectors';
import { DrillDownSectionBaseComponent } from './base/drill-down-section-base.component';
import { FYAlignmentMethod, DatabaseType } from '../../../../_study_constants';
import {
  loadFinancial,
  reloadFinancialInitial,
  updateFinancialDataCurrentConsolidated,
  updateFinancialDataCurrentCurrencyId,
  updateFinancialDataCurrentDatabaseId,
  updateFinancialDataSelectedConsolidated,
  updateFinancialDataSelectedCurrencyId,
  updateFinancialDataSelectedDatabaseId,
} from '../drill-down.actions';
import { DatePipe } from '@angular/common';
import _ from 'lodash-es';

@Component({
  selector: 'app-drill-down-financials',
  templateUrl: './financials.component.html',
  styleUrls: ['./financials.component.css'],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
})
export class FinancialsComponent extends DrillDownSectionBaseComponent implements OnInit, OnDestroy {
  financials: { [key: string]: any[] };
  headerRows: { cols: any }[];
  categoryNames: string[];
  currentDatabaseId: number;
  selectedDatabaseId: number;
  selectedCurrencyId: number;
  selectedConsolidated: boolean;
  isTypeOfFinancialsReadOnly: boolean;
  hasDatabaseFinancialDataLoaded: boolean;
  hasCurrencyFinancialDataLoaded: boolean;
  hasConsolidatedFinancialDataLoaded: boolean;
  initialLoad: boolean;
  isFinancialInitialLoaded: boolean;
  isLoading = false;
  yearAlignment: string;
  currencyData: {
    currencyUnit: string;
    studyCurrencyId: number;
    comparableCurrencyId: number;
    currencies: { currencyId: number; currencyName: string; currencyCode: string }[];
  };

  constructor(store: Store<AppState>, private datePipe: DatePipe) {
    super(store);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(hasFinancialInitialLoaded))
      .subscribe((loaded) => {
        this.isFinancialInitialLoaded = loaded;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectFinancialCategoryNames))
      .subscribe((names) => {
        this.categoryNames = names;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectFinancialDataCurrentDatabaseId))
      .subscribe((id) => {
        this.currentDatabaseId = id;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectFinancialDataSelectedDatabaseId))
      .subscribe((id) => {
        this.selectedDatabaseId = id;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectFinancialDataSelectedCurrencyId))
      .subscribe((id) => {
        this.selectedCurrencyId = id;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectFinancialDataSelectedConsolidated))
      .subscribe((consolidated) => {
        this.selectedConsolidated = consolidated;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(hasDatabaseFinancialDataLoaded))
      .subscribe((isLoaded) => {
        this.hasDatabaseFinancialDataLoaded = isLoaded;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(hasCurrencyFinancialDataLoaded))
      .subscribe((isLoaded) => {
        this.hasCurrencyFinancialDataLoaded = isLoaded;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(hasConsolidatedFinancialDataLoaded))
      .subscribe((isLoaded) => {
        this.hasConsolidatedFinancialDataLoaded = isLoaded;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectHasSingleTypeOfFinancialsFlag))
      .subscribe((hasSingle) => {
        this.isTypeOfFinancialsReadOnly = hasSingle;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectFinancialCurrencyData))
      .subscribe((data) => {
        this.currencyData = data;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectFinancialYearAlignmentData))
      .subscribe((data) => {
        if (data === undefined) {
          return;
        }

        this.yearAlignment =
          data.alignmentMethodId === FYAlignmentMethod.March31
            ? 'Mar 31<sup>st</sup> FY alignment'
            : `Tested party fiscal year end (${this.datePipe.transform(data.testedPartyFiscalYearEnd, 'dd-MMM-y')}) with ${
                data.monthsBack
              } months back`;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectFinancialData))
      .subscribe((data) => {
        this.initialLoad = data === undefined;

        if (this.initialLoad) {
          return;
        }

        this.isLoading = data.loading;
        const noData = _.isEmpty(data.item) || _.isEmpty(data.item.financials);

        this.headerRows = noData ? undefined : this.getHeaderRows(data.financialsRange, data.item.databaseAbbreviation);
        this.financials = noData ? undefined : data.item.financials;
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  getHeaderRows(financialsRange: number[], databaseAbbreviation: string): { cols: any }[] {
    return [
      {
        cols: this.getHeaderColumns(financialsRange),
      },
      {
        cols: this.getDataColumns(financialsRange, databaseAbbreviation),
      },
    ];
  }

  getHeaderColumns(range: number[]): any[] {
    const headerCols: any[] = [];

    range.forEach((year) => {
      headerCols.push({ label: year, colspan: this.currentDatabaseId === DatabaseType.CompustatNA ? 2 : 1 });
    });

    return headerCols;
  }

  getDataColumns(range: number[], abbreviation: string): any[] {
    const dataCols: any[] = [];

    range.forEach((year) => {
      if (this.currentDatabaseId === DatabaseType.CompustatNA) {
        dataCols.push({ field: `${abbreviation.toLowerCase()}o_${year}`, label: `${abbreviation}O` });
        dataCols.push({ field: `${abbreviation.toLowerCase()}r_${year}`, label: `${abbreviation}R` });
      } else {
        dataCols.push({ field: `${abbreviation.toLowerCase()}_${year}`, label: abbreviation });
      }
    });

    return dataCols;
  }

  getCurrencyText(currency): string {
    const additionalText =
      this.currencyData.currencies.length === 1
        ? '[Reference]'
        : currency.currencyId === this.currencyData.studyCurrencyId
        ? '[Reference]'
        : '[Functional]';
    return `${currency.currencyName} - ${currency.currencyCode} ${additionalText}`;
  }

  onDatabaseChange(id: string) {
    const databaseId = +id;
    const comparableId = this.companyData.comparableId;

    this.store.dispatch(updateFinancialDataSelectedDatabaseId({ id: comparableId, databaseId }));

    if (this.hasDatabaseFinancialDataLoaded) {
      this.store.dispatch(updateFinancialDataCurrentDatabaseId({ id: comparableId, databaseId }));
      return;
    }

    this.loadFinancialData(databaseId, this.selectedCurrencyId, this.selectedConsolidated);
  }

  onCurrencyChange(id: string) {
    const currencyId = +id;
    const comparableId = this.companyData.comparableId;

    this.store.dispatch(updateFinancialDataSelectedCurrencyId({ id: comparableId, currencyId }));

    if (this.hasCurrencyFinancialDataLoaded || _.isEmpty(this.financials)) {
      this.store.dispatch(updateFinancialDataCurrentCurrencyId({ id: comparableId, currencyId }));
      return;
    }

    this.loadFinancialData(this.selectedDatabaseId, currencyId, this.selectedConsolidated);
  }

  onTypeOfFinancialsChange(value: string) {
    const consolidated = value === 'true';
    const comparableId = this.companyData.comparableId;

    this.store.dispatch(updateFinancialDataSelectedConsolidated({ id: comparableId, consolidated }));

    if (this.hasConsolidatedFinancialDataLoaded || _.isEmpty(this.financials)) {
      this.store.dispatch(updateFinancialDataCurrentConsolidated({ id: comparableId, consolidated }));
      return;
    }

    this.loadFinancialData(this.selectedDatabaseId, this.selectedCurrencyId, consolidated);
  }

  reloadInitialFinancials() {
    this.store.dispatch(
      reloadFinancialInitial({
        id: this.companyData.comparableId,
        uniqueId: this.companyData.uniqueId,
        databaseId: this.selectedDatabaseId,
        consolidated: this.selectedConsolidated,
        currencyId: this.selectedCurrencyId,
        functionalCurrency: this.selectedCurrencyId === this.masterVendorCurrencyId,
      })
    );
  }

  loadFinancialData(databaseId: number, currencyId: number, consolidated: boolean) {
    this.store.dispatch(
      loadFinancial({
        id: this.companyData.comparableId,
        uniqueId: this.companyData.uniqueId,
        databaseId: databaseId,
        consolidated: consolidated,
        currencyId: currencyId,
        functionalCurrency: currencyId === this.masterVendorCurrencyId,
      })
    );
  }

  formatName(name: string): string {
    return _.upperFirst(name);
  }

  get masterVendorCurrencyId(): number {
    return this.currencyData.comparableCurrencyId;
  }

  get isNonBvd(): boolean {
    return (
      this.selectedDatabaseId === DatabaseType.OneSource ||
      this.selectedDatabaseId === DatabaseType.CompustatGlobal ||
      this.selectedDatabaseId === DatabaseType.CompustatNA
    );
  }
}
